import { useSeoMeta } from '@unhead/react';

export const normalizeCanonicalUrl = (value: string): string => {
  const normalized = value
    .trim()
    .replace(/^https?:\/\/viphousemovers\.com/i, 'https://www.viphousemovers.com')
    .replace(/^https?:\/\/www\.viphousemovers\.com/i, 'https://www.viphousemovers.com');

  const url = new URL(normalized || 'https://www.viphousemovers.com/', 'https://www.viphousemovers.com');

  if (url.pathname.length > 1 && url.pathname.endsWith('/')) {
    url.pathname = url.pathname.replace(/\/+$/, '');
  }

  return `${url.origin}${url.pathname}${url.search}${url.hash}`;
};

export interface PageMeta {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  robots?: string;
  canonical?: string;
  keywords?: string;
}

/**
 * Hook to set all SEO metadata for a page
 * Usage: useSEO({ title: 'Page Title', description: 'Page description' })
 */
export const useSEO = (meta: PageMeta) => {
  const canonical = normalizeCanonicalUrl(
    meta.canonical || (typeof window !== 'undefined' ? window.location.href : 'https://www.viphousemovers.com/')
  );

  useSeoMeta({
    title: meta.title,
    description: meta.description,
    ogTitle: meta.ogTitle || meta.title,
    ogDescription: meta.ogDescription || meta.description,
    ogImage: meta.ogImage,
    ogUrl: meta.ogUrl || canonical,
    robots: meta.robots || 'index, follow',
    canonical: canonical,
    keywords: meta.keywords,
    ogType: 'website',
    twitterCard: 'summary_large_image'
  });
};

/**
 * Common page metadata templates for VIP House Movers
 */
export const SEO_TEMPLATES = {
  home: {
    title: 'House Removals in Wolverhampton & Birmingham | VIP House Movers',
    description: 'Professional house removals, office removals, man and van services across Wolverhampton, Birmingham, Walsall, and more. Expert moving company with 4.8★ rating.',
    keywords: 'house removals wolverhampton, moving company birmingham, removals services midlands'
  },
  about: {
    title: 'About VIP House Movers | Professional Moving Services',
    description: 'Learn about VIP House Movers - trusted removals company serving the Midlands. Professional team, competitive prices, guaranteed quality service.',
    keywords: 'about us, moving company, removals service'
  },
  services: {
    title: 'Moving & Removal Services | VIP House Movers',
    description: 'Complete moving solutions including house removals, office removals, man and van, packing services, storage solutions, and more.',
    keywords: 'removal services, house moving, office removals, packing services'
  },
  contact: {
    title: 'Contact VIP House Movers | Get Your Free Quote',
    description: 'Contact us today for a free quote. Call now or fill out our online form to get started with your move.',
    keywords: 'contact removals company, free quote, get in touch'
  },
  reviews: {
    title: 'Customer Reviews | VIP House Movers | 4.8★ Rating',
    description: 'Read what our customers say about VIP House Movers. Check our 4.8 star reviews on Trustpilot and Google.',
    keywords: 'customer reviews, ratings, testimonials'
  },
  quote: {
    title: 'Free Moving Quote | VIP House Movers',
    description: 'Get an instant free quote for your move. Fast, reliable, and competitive prices from VIP House Movers.',
    keywords: 'free quote, moving estimate, pricing'
  },
  pricing: {
    title: 'Transparent Pricing | VIP House Movers',
    description: 'See our competitive pricing for house removals, office moves, man and van services, and more.',
    keywords: 'pricing, rates, cost of moving'
  },
  areasWeCover: {
    title: 'Areas We Serve | Removals Coverage | VIP House Movers',
    description: 'Serving Wolverhampton, Birmingham, Walsall, Dudley, Staffordshire, and surrounding areas. Check if we serve your location.',
    keywords: 'service areas, coverage, where we operate'
  },
  faq: {
    title: 'FAQ | Frequently Asked Questions | VIP House Movers',
    description: 'Find answers to common questions about our moving services, pricing, availability, and more.',
    keywords: 'frequently asked questions, faq, help'
  },
  blog: {
    title: 'Moving Tips & Guides | VIP House Movers Blog',
    description: 'Expert moving tips, packing guides, and helpful resources for your move.',
    keywords: 'moving tips, packing guides, removal advice'
  }
};

/**
 * Generate location-specific SEO metadata
 */
export const generateLocationMeta = (
  service: string,
  location: string,
  locationPostcode: string = ''
) => {
  const postcodeStr = locationPostcode ? ` ${locationPostcode}` : '';
  return {
    title: `${service} in ${location} | VIP House Movers${postcodeStr}`,
    description: `Professional ${service.toLowerCase()} services in ${location}. Book your move with VIP House Movers - trusted removals company serving ${location}${postcodeStr}.`,
    keywords: `${service.toLowerCase()} ${location}, removals ${location}, moving services ${location}`
  };
};

/**
 * Generate service-specific SEO metadata
 */
export const generateServiceMeta = (serviceName: string) => {
  const descriptions: Record<string, string> = {
    'House Removals': 'Complete house removal services with professional team. Fully insured and reliable house moving solutions.',
    'Office Removals': 'Professional office removal services. Minimize downtime with our efficient office moving solutions.',
    'Man and Van': 'Affordable man and van services for small moves, pickups, and deliveries.',
    'Packing Services': 'Professional packing services. We carefully pack and protect your belongings for safe transport.',
    'Furniture Assembly': 'Expert furniture assembly and disassembly services for your move.',
    'Storage Solutions': 'Secure self-storage solutions for short and long-term needs.',
    'Long Distance Removals': 'Long distance moving services across the UK. Professional and reliable nationwide coverage.',
    'Same Day Removals': 'Emergency same-day moving services. Quick turnaround times when you need them most.',
    'Student Moves': 'Affordable moving solutions designed for students. Perfect for term-time moves.',
    'Piano Removals': 'Specialized piano removal and transportation services by trained professionals.'
  };

  return {
    title: `${serviceName} | VIP House Movers`,
    description: descriptions[serviceName] || `Professional ${serviceName.toLowerCase()} services from VIP House Movers`,
    keywords: `${serviceName.toLowerCase()}, moving service, removals`
  };
};

/**
 * Build breadcrumb data structure for navigation
 */
export const buildBreadcrumbs = (path: string): Array<{ name: string; url: string }> => {
  const segments = path.split('/').filter(s => s);
  const breadcrumbs: Array<{ name: string; url: string }> = [
    { name: 'Home', url: '/' }
  ];

  let currentPath = '';
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const name = formatBreadcrumbName(segment);
    breadcrumbs.push({ name, url: currentPath });
  });

  return breadcrumbs;
};

/**
 * Format segment name for breadcrumb display
 */
export const formatBreadcrumbName = (segment: string): string => {
  return segment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
